import React from 'react';
import { 
  ShieldCheck, 
  HeartHandshake, 
  MapPin, 
  PhoneCall, 
  Clock, 
  Award, 
  Truck, 
  Sparkles, 
  ArrowLeft, 
  CheckCircle2, 
  Gem, 
  BookOpen, 
  Smile,
  PackageCheck,
  Check,
  Compass,
  Headphones
} from 'lucide-react';
import { DistributorConfig } from '../types';

interface AboutUsPageProps {
  distributor: DistributorConfig;
  onNavigateHome: () => void;
}

export const AboutUsPage: React.FC<AboutUsPageProps> = ({ distributor, onNavigateHome }) => {
  return (
    <div className="flex-1 bg-gradient-to-b from-slate-50 via-pink-50/20 to-white py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        
        {/* Navigation Breadcrumb */}
        <div className="mb-6 sm:mb-8 flex items-center justify-between">
          <button
            onClick={onNavigateHome}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-bold text-slate-700 bg-white hover:bg-slate-100 rounded-full transition-all active:scale-95 border border-slate-200 cursor-pointer shadow-xs"
          >
            <ArrowLeft className="w-4 h-4 text-[#E60067]" />
            <span>กลับสู่หน้าร้านหลัก</span>
          </button>
          <span className="text-[11px] sm:text-xs font-mono font-bold text-slate-500 bg-white px-3.5 py-1.5 rounded-full border border-slate-200 shadow-2xs">
            https://www.GIFFFORYOU.COM/aboutus
          </span>
        </div>

        {/* Hero Header Section */}
        <div className="text-center mb-10 sm:mb-14 relative">
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-black bg-pink-100 text-[#DB2777] mb-3 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            <span>เกี่ยวกับเรา • ABOUT US</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight leading-tight mb-3">
            ยินดีต้อนรับสู่ <span className="text-[#DB2777]">GIFFFORYOU.COM</span>
          </h1>
          <p className="text-sm sm:text-base text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">
            ศูนย์บริการและจำหน่ายผลิตภัณฑ์กิฟฟารีน (Giffarine) ครบวงจร มุ่งเน้นความสะดวกรวดเร็ว สินค้าของแท้ 100% ส่งตรงจากศูนย์ธุรกิจสาขาเดลิเวอรี่ถึงมือคุณ
          </p>
          <div className="w-16 h-1 bg-[#DB2777] mx-auto mt-5 rounded-full" />
        </div>

        {/* 3 Core Highlights Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-10">
          <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col items-center text-center hover:border-pink-300 transition-colors">
            <div className="w-12 h-12 bg-pink-50 rounded-2xl flex items-center justify-center text-[#DB2777] mb-3">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="text-sm sm:text-base font-black text-slate-900 mb-1">มาตรฐานของแท้ 100%</h3>
            <p className="text-xs text-slate-500 leading-relaxed">รับประกันสินค้าลิขสิทธิ์ถูกต้องจากกิฟฟารีน ประเทศไทย ไร้กังวลเรื่องของลอกเลียนแบบ</p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col items-center text-center hover:border-pink-300 transition-colors">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center text-emerald-600 mb-3">
              <Truck className="w-6 h-6" />
            </div>
            <h3 className="text-sm sm:text-base font-black text-slate-900 mb-1">ส่งตรงจากคลังเดลิเวอรี่</h3>
            <p className="text-xs text-slate-500 leading-relaxed">ควบคุมความสดใหม่ทุกล็อต แพ็คบรรจุห่อกันกระแทกอย่างดี ส่งด่วนถึงบ้านทั่วประเทศ</p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col items-center text-center hover:border-pink-300 transition-colors">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mb-3">
              <Headphones className="w-6 h-6" />
            </div>
            <h3 className="text-sm sm:text-base font-black text-slate-900 mb-1">บริการให้คำแนะนำเฉพาะบุคคล</h3>
            <p className="text-xs text-slate-500 leading-relaxed">พร้อมดูแลและตอบคำถามเกี่ยวกับผลิตภัณฑ์เสริมอาหารและสกินแคร์ด้วยความจริงใจ</p>
          </div>
        </div>

        {/* Main Content Articles */}
        <div className="space-y-6 text-slate-700 leading-relaxed">
          
          {/* Section 1: Our Origin & Commitment */}
          <section className="bg-white rounded-[24px] border border-slate-200/80 p-6 sm:p-10 shadow-xs space-y-4">
            <h2 className="text-lg sm:text-2xl font-black text-slate-900 flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <Gem className="w-6 h-6 text-[#DB2777]" />
              <span>ที่มาและพันธกิจของแพลตฟอร์ม</span>
            </h2>
            
            <div className="space-y-3.5 text-xs sm:text-sm text-slate-600 leading-relaxed text-justify">
              <p>
                <strong className="text-slate-900 font-bold">GIFFFORYOU.COM</strong> ถูกพัฒนาขึ้นโดยทีมงานนักธุรกิจตัวแทนจำหน่ายกิฟฟารีนรุ่นใหม่ ด้วยเจตนารมณ์ที่ต้องการให้ผู้บริโภคทั่วไปสามารถเข้าถึงผลิตภัณฑ์เพื่อสุขภาพและความงามคุณภาพสูงได้อย่างสะดวกรวดเร็ว โปร่งใส และมั่นใจได้ในความปลอดภัย 100%
              </p>
              <p>
                ผลิตภัณฑ์กิฟฟารีนทุกชิ้นผ่านการวิจัย พัฒนา และคัดสรรส่วนประกอบตามมาตรฐานสากล เป็นที่ไว้วางใจของคนไทยมานานกว่า 20 ปี เราจึงมุ่งมั่นนำเสนอนวัตกรรมช้อปปิ้งออนไลน์ที่ใช้งานง่ายบนทุกอุปกรณ์ เพื่อให้คุณได้รับผลิตภัณฑ์ล็อตใหม่ล่าสุดเสมอ
              </p>
            </div>
          </section>

          {/* Section 2: Direct Delivery Excellence */}
          <section className="bg-white rounded-[24px] border border-slate-200/80 p-6 sm:p-10 shadow-xs space-y-4">
            <h2 className="text-lg sm:text-2xl font-black text-slate-900 flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <PackageCheck className="w-6 h-6 text-[#DB2777]" />
              <span>ความมั่นใจในการจัดส่งตรงจากศูนย์ธุรกิจกิฟฟารีน</span>
            </h2>

            <div className="space-y-3.5 text-xs sm:text-sm text-slate-600 leading-relaxed text-justify">
              <p>
                แตกต่างจากการซื้อผ่านผู้ค้าออนไลน์ทั่วไป คำสั่งซื้อทุกรายการบน <strong className="text-slate-900 font-bold">GIFFFORYOU.COM</strong> จะถูกส่งคำสั่งตรงเข้าสู่ระบบบริการจัดส่งของสำนักงานธุรกิจสาขากิฟฟารีนในทันที สินค้าจึงได้รับการจัดเก็บในห้องควบคุมอุณหภูมิที่เหมาะสม และแพ็คส่งตรงถึงหน้าบ้านคุณ
              </p>
              
              <div className="my-4 p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <span className="text-xs sm:text-sm font-bold text-slate-800">
                  รับประกันความพึงพอใจ 7 วันเต็ม — หากพบปัญหาสินค้าชำรุดเสียหายระหว่างขนส่ง เราพร้อมดูแลเปลี่ยนชิ้นใหม่ให้ทันที
                </span>
              </div>
            </div>
          </section>

          {/* Section 3: Official Contact & Branch Info */}
          <section className="bg-white rounded-[24px] border border-slate-200/80 p-6 sm:p-10 shadow-xs space-y-6">
            <h2 className="text-lg sm:text-2xl font-black text-slate-900 flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <MapPin className="w-6 h-6 text-[#DB2777]" />
              <span>ข้อมูลติดต่อและสถานที่ทำการ</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3.5 text-xs sm:text-sm text-slate-600">
                <div className="flex gap-3">
                  <MapPin className="w-5 h-5 text-[#DB2777] shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 font-bold block mb-0.5">สำนักงานธุรกิจ สาขาเดลิเวอรี่:</strong>
                    <span>122/2 ซ. เรวดี แขวงพญาไท เขตพญาไท กรุงเทพมหานคร 10400</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <PhoneCall className="w-5 h-5 text-[#DB2777] shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 font-bold block mb-0.5">เบอร์โทรศัพท์ติดต่อ:</strong>
                    <a href="tel:026195222" className="text-[#DB2777] font-extrabold hover:underline text-base">
                      02 619 5222
                    </a>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Clock className="w-5 h-5 text-[#DB2777] shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-slate-900 font-bold block mb-0.5">เวลาทำการ:</strong>
                    <span>เปิดทำการทุกวัน 09:00 - 19:00 น. (จัดส่งรอบบ่ายทุกวัน)</span>
                  </div>
                </div>
              </div>

              {/* LINE Add Friend Box */}
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col justify-between space-y-3">
                <div>
                  <span className="text-xs font-mono font-bold text-slate-500 block mb-1">OFFICIAL LINE SERVICE</span>
                  <strong className="text-slate-900 text-sm block">แอดไลน์เพื่อติดตามพัสดุและปรึกษาสุขภาพ</strong>
                  <p className="text-xs text-slate-500 mt-1">
                    LINE ID: <strong className="text-emerald-700">@997wvsow</strong>
                  </p>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-center gap-3">
                  <a 
                    href="https://lin.ee/PSM7qHqg" 
                    target="_blank" 
                    rel="noreferrer" 
                    className="inline-block transition-transform hover:scale-[1.03] active:scale-95"
                  >
                    <img 
                      src="https://scdn.line-apps.com/n/line_add_friends/btn/th.png" 
                      alt="เพิ่มเพื่อน" 
                      height="36" 
                      style={{ border: 0, height: '36px' }} 
                    />
                  </a>
                  <a
                    href="tel:026195222"
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all active:scale-95"
                  >
                    <PhoneCall className="w-3.5 h-3.5" />
                    <span>โทรสายตรง</span>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Call to Action Banner */}
          <section className="bg-slate-900 rounded-[24px] p-6 sm:p-8 text-white relative overflow-hidden shadow-lg">
            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-base sm:text-xl font-black text-white mb-1">
                  เริ่มต้นเลือกซื้อสินค้าคุณภาพ หรือสมัครสมาชิกลด 25%
                </h3>
                <p className="text-xs sm:text-sm text-slate-300">
                  ช้อปสะดวก สินค้าส่งตรงถึงบ้าน พร้อมการดูแลมาตรฐานกิฟฟารีนแท้ 100%
                </p>
              </div>
              <button
                onClick={onNavigateHome}
                className="px-6 py-3 bg-gradient-to-r from-[#E60067] to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-black text-xs sm:text-sm rounded-xl transition-all shadow-md active:scale-95 cursor-pointer whitespace-nowrap"
              >
                เข้าสู่หน้าร้านค้า
              </button>
            </div>
          </section>

        </div>

      </div>
    </div>
  );
};
